import { trackError } from '@billy/track-error'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FEC_IMPORT_ANALYSIS_TIMEOUT_MS } from '../constants/fecImportAnalysis'
import type { FecImportConfirmationKind } from '../enums/fecImportConfirmationKind'
import { FecImportLifecycleStatus } from '../enums/fecImportLifecycleStatus'
import type { FecImportItem } from '../query-api'
import { appendUniqueIds } from '../utils/appendUniqueIds'
import { getFecImportConfirmationKind } from '../utils/getFecImportConfirmationKind'
import { isFecImportAnalysisFailed } from '../utils/isFecImportAnalysisFailed'
import { isFecImportInProgress } from '../utils/isFecImportInProgress'
import { isFecImportOpenForWizard } from '../utils/isFecImportOpenForWizard'
import { isFecImportRetryable } from '../utils/isFecImportRetryable'
import { isFecImportTimeoutFetchError } from '../utils/isFecImportTimeoutFetchError'
import { useFecImportAnalysisTracking } from './useFecImportAnalysisTracking'
import { useFecImportFetchErrorTracking } from './useFecImportFetchErrorTracking'
import { useFecImportSessionActions } from './useFecImportSessionActions'
import { useFetchFecImports } from './useFetchFecImports'
import { useRetriedFecImports } from './useRetriedFecImports'

export type FecImportWizardStep =
  | 'analysing'
  | 'confirmation'
  | 'load-error'
  | 'mapping'
  | 'restoring'
  | 'resume'
  | 'upload'

export interface UseFecImportSessionResult {
  analysingFecImports: FecImportItem[]
  cancellingFecImportIds: string[]
  canRetryAnalysis: boolean
  confirmationKind?: FecImportConfirmationKind
  isMappingsConfirmed: boolean
  isReuploading: boolean
  isRetryingAnalysis: boolean
  onBackFromMappingReview: () => void
  onCancelAnalysingFile: (fecImportId: string) => Promise<void>
  onImportCreated: (importId: string) => void
  setMappingsConfirmed: () => void
  onResetSession: () => void
  onResumeImports: (importIds: string[]) => Promise<void>
  onRetryAnalysis: () => Promise<void>
  onReupload: () => Promise<void>
  onSkipFailedFileContinue: () => Promise<void>
  onStartMappingReview: () => void
  onUploadComplete: (files: File[]) => void
  refetch: () => void
  /** Imports found waiting for review when the wizard opened; shown on the resume screen. */
  resumableFecImports: FecImportItem[]
  sessionFecImports: FecImportItem[]
  sessionFiles: File[]
  step: FecImportWizardStep
}

const getWizardStep = ({
  confirmationKind,
  isAnalysingView,
  isLoadError,
  isResumable,
  isRestoringSession,
  isReviewingMappings,
  isRetryingAnalysis,
}: {
  confirmationKind?: FecImportConfirmationKind
  isAnalysingView: boolean
  isLoadError: boolean
  isResumable: boolean
  isRestoringSession: boolean
  isReviewingMappings: boolean
  isRetryingAnalysis: boolean
}): FecImportWizardStep => {
  if (isLoadError) {
    return 'load-error'
  }

  if (isRestoringSession) {
    return 'restoring'
  }

  if (isRetryingAnalysis) {
    return 'analysing'
  }

  if (isReviewingMappings && confirmationKind === 'success') {
    return 'mapping'
  }

  if (confirmationKind) {
    return 'confirmation'
  }

  if (isAnalysingView) {
    return 'analysing'
  }

  if (isResumable) {
    return 'resume'
  }

  return 'upload'
}

const getSessionImports = (fecImports: FecImportItem[], sessionImportIds: string[]): FecImportItem[] =>
  fecImports.filter((fecImport) => sessionImportIds.includes(fecImport.id))

const isSessionWaitingForAnalysis = (fecImports: FecImportItem[], sessionImportIds: string[]): boolean => {
  if (sessionImportIds.length === 0) {
    return false
  }

  const sessionImports = getSessionImports(fecImports, sessionImportIds)

  return sessionImports.length !== sessionImportIds.length || sessionImports.some(isFecImportInProgress)
}

// The backend can still finish a detection it marked failed (stuck recovery), so keep looking at those, with
// growing pauses so a failure left on screen does not keep the backend busy forever.
const hasFailedDetection = (fecImports: FecImportItem[], sessionImportIds: string[]): boolean =>
  getSessionImports(fecImports, sessionImportIds).some(
    (fecImport) => fecImport.status === FecImportLifecycleStatus.DetectionFailed,
  )

export const useFecImportSession = (): UseFecImportSessionResult => {
  const hasExplicitSessionImportIdsRef = useRef(false)
  const [sessionImportIds, setSessionImportIds] = useState<string[]>([])
  const [isMappingsConfirmed, setIsMappingsConfirmed] = useState(false)
  const [hasEnteredAnalysingView, setHasEnteredAnalysingView] = useState(false)
  const [ignoreOrgInProgressImports, setIgnoreOrgInProgressImports] = useState(false)
  const [isReviewingMappings, setIsReviewingMappings] = useState(false)
  const [sessionFiles, setSessionFiles] = useState<File[]>([])
  const [hasAnalysisTimedOut, setHasAnalysisTimedOut] = useState(false)
  const hasRetriedImportsRef = useRef(false)
  // Undefined until the open imports were read once. Then: the ids waiting on the resume screen (maybe none), and
  // the ids that had already failed, which are shown but not reported to error tracking again.
  const [coldStart, setColdStart] = useState<{ failedIds: string[]; resumeIds: string[] }>()
  const analysisWaitStartedAtRef = useRef<number | null>(null)
  const hasReportedAnalysisTimeoutRef = useRef(false)
  const fetchErrorTracking = useFecImportFetchErrorTracking()
  const {
    error,
    fecImports: serverFecImports,
    isError,
    isFetchedAfterMount,
    isRefetchError,
    refetch,
  } = useFetchFecImports({
    onError: fetchErrorTracking.onError,
    onSuccess: fetchErrorTracking.onSuccess,
    // What is open is decided on the server, never from a cached answer of an earlier visit.
    refetchOnMount: 'always',
    shouldPoll: (imports) =>
      !hasAnalysisTimedOut &&
      !isMappingsConfirmed &&
      (hasRetriedImportsRef.current || isSessionWaitingForAnalysis(imports, sessionImportIds)),
    shouldPollWithBackoff: (imports) =>
      !hasAnalysisTimedOut && !isMappingsConfirmed && hasFailedDetection(imports, sessionImportIds),
  })

  const { fecImports, markImportsRetried } = useRetriedFecImports({
    hasAnalysisTimedOut,
    hasRetriedImportsRef,
    serverFecImports,
    sessionImportIds,
  })

  const openFecImports = useMemo(() => fecImports.filter(isFecImportOpenForWizard), [fecImports])
  const inProgressFecImports = useMemo(() => openFecImports.filter(isFecImportInProgress), [openFecImports])
  const sessionFecImports = useMemo(() => {
    const seenIds = new Set<string>()

    return fecImports.filter((fecImport) => {
      if (!sessionImportIds.includes(fecImport.id) || seenIds.has(fecImport.id)) {
        return false
      }

      seenIds.add(fecImport.id)
      return true
    })
  }, [fecImports, sessionImportIds])
  const serverConfirmationKind = getFecImportConfirmationKind({
    hasEnteredAnalysingView,
    isRefetchError,
    isTimeoutFetchError: isFecImportTimeoutFetchError(error),
    sessionFecImports,
    sessionImportIds,
  })
  const confirmationKind = serverConfirmationKind ?? (hasAnalysisTimedOut ? 'timeout' : undefined)
  const canRetryAnalysis = isRefetchError || hasAnalysisTimedOut || sessionFecImports.some(isFecImportRetryable)
  const isRestoringSession = coldStart === undefined && !isError && !isRefetchError
  const isUploadSessionPending = sessionImportIds.length > 0 && !hasEnteredAnalysingView
  const isAnalysingView =
    !confirmationKind &&
    !isUploadSessionPending &&
    (hasEnteredAnalysingView || (!ignoreOrgInProgressImports && inProgressFecImports.length > 0))
  const analysingFecImports =
    sessionImportIds.length > 0 || ignoreOrgInProgressImports ? sessionFecImports : inProgressFecImports
  const resumableFecImports = useMemo(
    () =>
      sessionImportIds.length === 0 && coldStart
        ? openFecImports.filter((fecImport) => coldStart.resumeIds.includes(fecImport.id))
        : [],
    [coldStart, openFecImports, sessionImportIds.length],
  )
  const trackableFecImports = useMemo(
    () => sessionFecImports.filter((fecImport) => !coldStart?.failedIds.includes(fecImport.id)),
    [coldStart, sessionFecImports],
  )
  const { resetReportedAnalysisErrors } = useFecImportAnalysisTracking(trackableFecImports)
  // Once the mappings are finalised the imports leave the open list as they get pushed, which is not a wait.
  const isWaitingForAnalysis = !isMappingsConfirmed && isSessionWaitingForAnalysis(fecImports, sessionImportIds)

  const resetAnalysisTimeout = useCallback(() => {
    analysisWaitStartedAtRef.current = null
    hasReportedAnalysisTimeoutRef.current = false
    setHasAnalysisTimedOut(false)
  }, [])

  // Cold start: whatever the server still has open decides where the wizard begins. Imports waiting for review get
  // the resume screen; anything else (still running, failed, or a mix) is adopted into the session so the analysing
  // or confirmation view reports it instead of leaving it invisible.
  useEffect(() => {
    if (coldStart !== undefined || !isFetchedAfterMount || isError) {
      return
    }

    const areAllWaitingForReview =
      openFecImports.length > 0 &&
      openFecImports.every((fecImport) => fecImport.status === FecImportLifecycleStatus.InReview)

    if (!hasExplicitSessionImportIdsRef.current && openFecImports.length > 0 && !areAllWaitingForReview) {
      setHasEnteredAnalysingView(true)
      setSessionImportIds(openFecImports.map((fecImport) => fecImport.id))
    }

    setColdStart({
      failedIds: openFecImports.filter(isFecImportAnalysisFailed).map((fecImport) => fecImport.id),
      resumeIds: areAllWaitingForReview ? openFecImports.map((fecImport) => fecImport.id) : [],
    })
  }, [coldStart, isError, isFetchedAfterMount, openFecImports])

  useEffect(() => {
    if (hasAnalysisTimedOut || !isWaitingForAnalysis) {
      if (!hasAnalysisTimedOut) {
        analysisWaitStartedAtRef.current = null
      }

      return
    }

    analysisWaitStartedAtRef.current ??= Date.now()

    const remainingMs = FEC_IMPORT_ANALYSIS_TIMEOUT_MS - (Date.now() - analysisWaitStartedAtRef.current)

    const markTimedOut = () => {
      if (!hasReportedAnalysisTimeoutRef.current) {
        hasReportedAnalysisTimeoutRef.current = true
        trackError(new Error(`FEC import analysis timed out waiting for detection: ${sessionImportIds.join(',')}`))
      }

      setHasAnalysisTimedOut(true)
    }

    if (remainingMs <= 0) {
      markTimedOut()
      return
    }

    const timeoutId = globalThis.setTimeout(markTimedOut, remainingMs)

    return () => {
      globalThis.clearTimeout(timeoutId)
    }
  }, [hasAnalysisTimedOut, isWaitingForAnalysis, sessionImportIds])

  // An import that starts running while the wizard is open (e.g. uploaded in another tab) joins the session.
  useEffect(() => {
    if (
      coldStart === undefined ||
      ignoreOrgInProgressImports ||
      hasExplicitSessionImportIdsRef.current ||
      isUploadSessionPending ||
      sessionImportIds.length > 0
    ) {
      return
    }

    const inProgressIds = inProgressFecImports.map((fecImport) => fecImport.id)

    if (inProgressIds.length === 0) {
      return
    }

    setHasEnteredAnalysingView(true)
    setSessionImportIds((currentIds) => appendUniqueIds(currentIds, inProgressIds))
  }, [coldStart, ignoreOrgInProgressImports, inProgressFecImports, isUploadSessionPending, sessionImportIds.length])

  const onImportCreated = useCallback((importId: string) => {
    hasExplicitSessionImportIdsRef.current = true
    setSessionImportIds((currentIds) => appendUniqueIds(currentIds, [importId]))
  }, [])

  const onResumeImports = useCallback(
    async (importIds: string[]) => {
      // Confirmation is derived from the list, so make sure it already holds these imports. Entering the
      // session first would show "analysing" until the next poll whenever the cached list is stale.
      await refetch()

      hasExplicitSessionImportIdsRef.current = true
      setSessionImportIds((currentIds) => appendUniqueIds(currentIds, importIds))
      setHasEnteredAnalysingView(true)
      setIsReviewingMappings(true)
    },
    [refetch],
  )

  const onUploadComplete = useCallback(
    (files: File[]) => {
      resetAnalysisTimeout()
      setSessionFiles(files)
      setHasEnteredAnalysingView(true)
    },
    [resetAnalysisTimeout],
  )

  const {
    cancellingFecImportIds,
    isReuploading,
    isRetryingAnalysis,
    onCancelAnalysingFile,
    onRetryAnalysis,
    onReupload,
    onSkipFailedFileContinue,
  } = useFecImportSessionActions({
    hasExplicitSessionImportIdsRef,
    isRefetchError,
    markImportsRetried,
    refetch,
    resetReportedAnalysisErrors,
    sessionFecImports,
    sessionImportIds,
    setHasEnteredAnalysingView,
    setIgnoreOrgInProgressImports,
    setSessionFiles,
    setSessionImportIds,
  })

  const handleRetryAnalysis = useCallback(async () => {
    resetAnalysisTimeout()
    await onRetryAnalysis()
  }, [onRetryAnalysis, resetAnalysisTimeout])

  const handleReupload = useCallback(async () => {
    resetAnalysisTimeout()
    await onReupload()
  }, [onReupload, resetAnalysisTimeout])

  const onStartMappingReview = useCallback(() => {
    setIsReviewingMappings(true)
  }, [])

  const onBackFromMappingReview = useCallback(() => {
    setIsReviewingMappings(false)
  }, [])

  const setMappingsConfirmed = useCallback(() => {
    setIsMappingsConfirmed(true)
  }, [])

  const onResetSession = useCallback(() => {
    resetAnalysisTimeout()
    setHasEnteredAnalysingView(false)
    setIsReviewingMappings(false)
    setSessionFiles([])
    setSessionImportIds([])
  }, [resetAnalysisTimeout])

  const step = getWizardStep({
    confirmationKind,
    isAnalysingView,
    isLoadError: isError,
    isResumable: resumableFecImports.length > 0,
    isRestoringSession,
    isReviewingMappings,
    isRetryingAnalysis,
  })

  return {
    analysingFecImports,
    cancellingFecImportIds,
    canRetryAnalysis,
    confirmationKind,
    isMappingsConfirmed,
    isReuploading,
    isRetryingAnalysis,
    onBackFromMappingReview,
    onCancelAnalysingFile,
    onImportCreated,
    setMappingsConfirmed,
    onResetSession,
    onResumeImports,
    onRetryAnalysis: handleRetryAnalysis,
    onReupload: handleReupload,
    onSkipFailedFileContinue,
    onStartMappingReview,
    onUploadComplete,
    refetch,
    resumableFecImports,
    sessionFecImports,
    sessionFiles,
    step,
  }
}
